const fallbackFontFamily = `Arial, Helvetica, sans-serif, "Segoe UI Emoji", "Segoe UI Symbol"`;

export const typography = {
    fontFamily: {
        /**
         * For most body styles.
         */
        inter: `"Inter var", ${fallbackFontFamily}`,

        /**
         * For some headings and other text that needs to be a bit bolder.
         */
        ttNorms: `"TT Norms Pro Bold", ${fallbackFontFamily}`,
    },
    fontSize: {
        size10: "10px",
        size20: "12px",
        size30: "14px",
        size40: "16px",
        size50: "20px",
        size60: "24px",
        size70: "28px",
        size80: "32px",
    },
    // Font size * 1.2, rounded to the next nearest even number
    lineHeight: {
        size10: "12px",
        size20: "16px",
        size30: "18px",
        size40: "20px",
        size50: "24px",
        size60: "30px",
        size70: "34px",
        size80: "46px",
    },
    fontWeight: {
        bold: 700,
        medium: 500,
        regular: 400,
        semiBold: 600,
    },
} as const;

type TypeSizeNames = keyof typeof typography.fontSize;

interface ObjectLine<Size extends TypeSizeNames> {
    fontSize: (typeof typography.fontSize)[Size];
    lineHeight: (typeof typography.lineHeight)[Size];
}

type LineSizeReturn<Size extends TypeSizeNames, Style extends "object" | "css"> = Style extends "object"
    ? ObjectLine<Size>
    : string;

/**
 * Returns a font-size and line-height combo to use for each size key.
 */
// Generics here provide better intellisense:
//
// const x: {
//   fontSize: "14px";
//   lineHeight: "18px";
// } = lineSize('size30')
// TODO use this or delete it. Left here since we're considering it.
const _lineSize = <
    Size extends TypeSizeNames,
    Style extends "object" | "css",
    ReturnValue extends LineSizeReturn<Size, Style>
>(
    size: Size,
    style: Style
): ReturnValue => {
    const result = {
        fontSize: typography.fontSize[size],
        lineHeight: typography.lineHeight[size],
    } as const;
    switch (style) {
        case "css":
            return `
                font-size: ${result.fontSize};
                line-height: ${result.lineHeight};
            ` as ReturnValue;
        case "object":
        default:
            return result as ReturnValue;
    }
};
